import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Tag as TagIcon } from "lucide-react";
import { listarTags, criarTag, atualizarTag, excluirTag, Tag } from "../services/tags";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";
import { TagChip } from "../components/TagChip";

export function Tags() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  // Gestor e admin gerenciam tags; exclusão fica só com admin.
  if (!can(["gestor", "admin"])) return <Navigate to="/" replace />;
  const podeEditar = can(["gestor", "admin"]);
  const ehAdmin = can(["admin"]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["tags", "todos"],
    queryFn: () => listarTags({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: criarTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags"] });
      setNovo(false);
    },
  });

  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarTag(id, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags"] });
      setEditandoId(null);
    },
  });

  const excluirM = useMutation({
    mutationFn: excluirTag,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
    onError: (e: any) => {
      const msg = e?.response?.data?.error?.message || "Erro ao excluir";
      confirm({ title: "Não foi possível excluir", message: msg, confirmText: "OK" });
    },
  });

  async function aoExcluir(t: Tag) {
    const ok = await confirm({
      title: `Excluir tag "${t.nome}"?`,
      message: "Se houver tarefas usando essa tag, a exclusão será bloqueada — desative em vez disso.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(t.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <TagIcon size={20} /> Tags
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Etiquetas para classificar tarefas. Qualquer usuário cria uma tag; gestor/admin edita; só admin exclui.
          </p>
        </div>
        <button
          onClick={() => setNovo(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Nova tag
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Preview</th>
              <th className="text-left px-4 py-2 font-medium">Nome</th>
              <th className="text-left px-4 py-2 font-medium">Slug</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && (
              <LinhaEditavel
                tag={null}
                onSalvar={(dados) => criarM.mutate(dados)}
                onCancelar={() => setNovo(false)}
                salvando={criarM.isPending}
              />
            )}
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">Carregando…</td>
              </tr>
            )}
            {!isLoading && tags.length === 0 && !novo && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nenhuma tag cadastrada.</td>
              </tr>
            )}
            {tags.map((t) =>
              editandoId === t.id ? (
                <LinhaEditavel
                  key={t.id}
                  tag={t}
                  onSalvar={(dados) => atualizarM.mutate({ id: t.id, dados })}
                  onCancelar={() => setEditandoId(null)}
                  salvando={atualizarM.isPending}
                />
              ) : (
                <tr key={t.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2">
                    <TagChip tag={t} />
                  </td>
                  <td className="px-4 py-2 font-medium">{t.nome}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{t.slug}</td>
                  <td className="px-4 py-2 tabular-nums">{t.ordem}</td>
                  <td className="px-4 py-2">
                    {t.ativo ? (
                      <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativa</span>
                    ) : (
                      <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativa</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {podeEditar ? (
                      <>
                        <button
                          onClick={() => atualizarM.mutate({ id: t.id, dados: { ativo: !t.ativo } })}
                          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                          title={t.ativo ? "Desativar" : "Ativar"}
                        >
                          {t.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => setEditandoId(t.id)}
                          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                          title="Editar"
                        >
                          <Pencil size={14} />
                        </button>
                        {ehAdmin && (
                          <button
                            onClick={() => aoExcluir(t)}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Excluir"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">só gestor/admin</span>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaEditavel({ tag, onSalvar, onCancelar, salvando }: {
  tag: Tag | null;
  onSalvar: (dados: { nome: string; cor: string; icone: string; ordem: number; ativo: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [nome, setNome] = useState(tag?.nome || "");
  const [cor, setCor] = useState(tag?.cor || "#2563eb");
  const [icone, setIcone] = useState(tag?.icone || "🏷️");
  const [ordem, setOrdem] = useState(tag?.ordem ?? 0);
  const [ativo, setAtivo] = useState(tag?.ativo ?? true);

  function salvar() {
    if (!nome.trim()) return;
    onSalvar({ nome: nome.trim(), cor, icone: icone || "🏷️", ordem: Number(ordem) || 0, ativo });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <div className="flex items-center gap-1.5">
          <input
            type="color"
            value={cor}
            onChange={(e) => setCor(e.target.value)}
            className="w-8 h-7 rounded cursor-pointer border border-slate-300"
            title="Cor"
          />
          <input
            type="text"
            value={icone}
            onChange={(e) => setIcone(e.target.value)}
            placeholder="🏷️"
            maxLength={4}
            className="w-12 h-7 rounded text-center border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-base"
            title="Emoji (cole um emoji do teclado)"
          />
        </div>
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex.: Urgente"
          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
          autoFocus
        />
      </td>
      <td className="px-4 py-2 font-mono text-xs text-slate-400">
        {tag ? tag.slug : <em>(automático)</em>}
      </td>
      <td className="px-4 py-2">
        <input
          type="number"
          value={ordem}
          onChange={(e) => setOrdem(Number(e.target.value) || 0)}
          className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          Ativa
        </label>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={salvar} disabled={!nome.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar">
          <Check size={14} />
        </button>
        <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar">
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}
